import { Dictionary } from 'lodash';
import moment, { Moment } from 'moment';
import React, { FC, useCallback, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Role, Team, User } from 'api/models';
import {
  PlanningLevelConfig,
  ResourceType,
} from 'api/resource-management/types';
import { confirm } from 'components/atoms/modal';
import { FilterBar } from 'components/molecules/filter-bar';
import { PlaceholderCreateOrEditModal } from 'components/organisms/placeholders/create-edit-modal';
import {
  AddTeamsModal,
  AddUsersModal,
} from 'components/organisms/resource-management/add-to-project';
import { ManageAllocationDrawer } from 'components/organisms/resource-management/manage-allocation';
import {
  useAllocationManager,
  useAutoAllocationStateManager,
  useResourceManager,
} from 'components/organisms/resource-management/managers';
import {
  SchedulerResourceType,
  TeamAsLeafResource,
  UserAsLeafResource,
} from 'components/organisms/resource-management/resource-types';
import { useTeamRemovedFromProject } from 'components/organisms/resource-management/scheduler-config/listeners';
import { ViewPresetId } from 'components/organisms/resource-management/scheduler-config/view-presets/constants';
import {
  BulkActionPayloadMap,
  GroupBy,
  ProjectsWithMembers,
} from 'components/organisms/resource-management/types';
import { resourceIdForProject } from 'components/organisms/resource-management/utils/id';
import { DefaultWorkingDayConfig } from 'components/organisms/user-capacity';
import { setDirtyState } from 'data/templates';
import {
  AllocatedMembers,
  updateProjectTemplate,
  useActiveProjectTemplate,
} from 'data/templates/projects';
import {
  allocationsBulkAction,
  updateAllocatedMembersAction,
} from 'data/templates/projects/actions';
import { selectDirtyState } from 'data/templates/selectors';
import { useTranslate } from 'hooks/use-translation';
import { useResourceManagementConfigQuery } from 'queries/resource-management-configuration';
import { floatUid } from 'utils/uid';
import { AutoAllocateSourceTasksDrawer } from './auto-allocation-source-tasks';
import {
  useAddTeamsManager,
  useAddUsersManager,
  useAutoAllocateSourceTasks,
  useCommonManagerProps,
  useDeleteTeamsManager,
  useEditTeamsManager,
  useProjectInfoProvider,
  useRefreshAllocationsFromTasks,
  useRemoveUsersManager,
  useSchedulerInitialize,
} from './managers';
import { Scheduler } from './scheduler';
import {
  getZoomPresets,
  getZoomPresetsForHoursPerWeek,
  useColumnsConfig,
  useEventsTooltip,
} from './scheduler-config';
import { AutoAllocateSwitch, Toolbar } from './toolbar';
import { TemplateAssignee } from './types';
import { AllWorkingDayConfig } from './utils/working-days';

interface Props {
  roles: Role[];
  relativeDate: Moment;
  project: ProjectsWithMembers;
  projectLookup: Dictionary<ProjectsWithMembers>;
  userLookup: Dictionary<User>;
  teamLookup: Dictionary<Team>;
  teamMembership: Record<string, Team[]>;
  allocatedMembers: AllocatedMembers;
  allTasksAssignees: TemplateAssignee;
  autoAllocate: boolean;
}

export const ResourceManagementContent: FC<Props> = ({
  relativeDate,
  roles,
  project,
  teamMembership,
  userLookup,
  teamLookup,
  projectLookup,
  allocatedMembers,
  allTasksAssignees,
  autoAllocate,
}) => {
  const { data: resourceManagementConfig } = useResourceManagementConfigQuery();

  const isHoursPerWeek = useMemo(
    () =>
      resourceManagementConfig?.planningLevelConfig ===
      PlanningLevelConfig.HoursPerWeek,
    [resourceManagementConfig?.planningLevelConfig],
  );

  const viewPresets = useMemo(
    () =>
      isHoursPerWeek
        ? getZoomPresetsForHoursPerWeek(relativeDate)
        : getZoomPresets(relativeDate),
    [relativeDate, isHoursPerWeek],
  );

  const workingDaysConfig = useMemo(
    () => (isHoursPerWeek ? DefaultWorkingDayConfig : AllWorkingDayConfig),
    [isHoursPerWeek],
  );

  const templateDirtyState = useSelector(selectDirtyState(project.projectId));
  const translate = useTranslate();
  const schedulerRef = useRef<any>();
  const { getSchedulerProjectInfo, getMaximumDateForScheduler } =
    useProjectInfoProvider({
      roles,
      project,
      teamMembership,
      workingDaysConfig,
      relativeDate,
      userLookup,
      teamLookup,
      projectLookup,
      isHoursPerWeek,
    });
  const schedulerConfiguration = useMemo(
    () => ({
      infiniteScroll: false,
      startDate: relativeDate.startOf('day').add(1, 'day').toDate(),
      endDate: getMaximumDateForScheduler()
        .endOf(isHoursPerWeek ? 'year' : 'day')
        .toDate(),
      viewPreset: isHoursPerWeek
        ? ViewPresetId.WeekZoomed
        : ViewPresetId.DayZoomed,
      features: {
        eventDrag: {
          tooltipTemplate: ({ startDate, endDate }) =>
            isHoursPerWeek
              ? translate(
                  'templates.resource_management.tooltips.dates.hours_per_week',
                  {
                    startWeek: moment(startDate).diff(relativeDate, 'week') + 1,
                    duration:
                      moment(endDate).diff(moment(startDate), 'week') + 1,
                  },
                )
              : translate('templates.resource_management.tooltips.dates', {
                  startDay: moment(startDate).diff(relativeDate, 'day'),
                  duration: moment(endDate).diff(moment(startDate), 'day') + 1,
                }),
        },
        eventResize: {
          tooltipTemplate: ({ startDate, endDate }) =>
            isHoursPerWeek
              ? translate(
                  'templates.resource_management.tooltips.dates.hours_per_week',
                  {
                    startWeek: moment(startDate).diff(relativeDate, 'week') + 1,
                    duration:
                      moment(endDate)
                        .add(1, 'minute')
                        .subtract(1, 'day')
                        .diff(moment(startDate), 'week') + 1,
                  },
                )
              : translate('templates.resource_management.tooltips.dates', {
                  startDay: moment(startDate).diff(relativeDate, 'day'),
                  duration:
                    moment(endDate)
                      .add(1, 'minute')
                      .subtract(1, 'day')
                      .diff(moment(startDate), 'day') + 1,
                }),
        },
      },
      listeners: {
        beforeeventdropfinalize: (params) => {
          const {
            context: { origEnd, origStart, startDate, endDate, eventRecord },
          } = params;

          // eslint-disable-next-line no-param-reassign
          params.context.async = true;
          params.context.finalize(false);

          schedulerRef.current.instance.trigger('onCustomEventDrag', {
            event: eventRecord,
            from: {
              startDate: origStart,
              endDate: origEnd,
            },
            to: {
              startDate: moment(startDate).isBefore(relativeDate.add(1, 'day'))
                ? relativeDate.add(1, 'day').toDate()
                : startDate,
              endDate,
            },
          });
        },
      },
    }),
    [getMaximumDateForScheduler, relativeDate, isHoursPerWeek, translate],
  );

  const dispatch = useDispatch();
  const saveNewlyAllocatedMembersToState = useCallback(
    (actionsMap: BulkActionPayloadMap) => {
      const createPayload = actionsMap.create;
      const tempIds = Object.keys(createPayload);

      if (tempIds.length > 0) {
        const newlyAllocatedMembers = tempIds.reduce(
          (members, id) => {
            const allocation = createPayload[id];

            if (
              allocation.type === ResourceType.User &&
              allocatedMembers.users.find(
                (x) => x.userId === allocation.userId,
              ) == null
            ) {
              members.users.push(userLookup[allocation.userId]);
            }
            if (
              allocation.type === ResourceType.Team &&
              allocatedMembers.teams.find(
                (x) => x.teamId === allocation.teamId,
              ) == null
            ) {
              members.teams.push(teamLookup[allocation.teamId]);
            }

            return members;
          },
          {
            users: [],
            teams: [],
          } as AllocatedMembers,
        );

        if (
          newlyAllocatedMembers.users.length > 0 ||
          newlyAllocatedMembers.teams.length > 0
        ) {
          dispatch(
            updateAllocatedMembersAction({
              users: allocatedMembers.users.concat(newlyAllocatedMembers.users),
              teams: allocatedMembers.teams.concat(newlyAllocatedMembers.teams),
            }),
          );
        }
      }
    },
    [
      allocatedMembers.teams,
      allocatedMembers.users,
      dispatch,
      teamLookup,
      userLookup,
    ],
  );
  const removeTaskNotassignedMembers = useCallback(() => {
    const resource =
      schedulerRef.current.instance.project.resourceStore.getById(
        resourceIdForProject(project.projectId),
      );

    [...resource.children].forEach((child) => {
      const { type, children: roleChildren } = child;

      if (type === SchedulerResourceType.Role) {
        (
          (roleChildren ?? []) as (UserAsLeafResource | TeamAsLeafResource)[]
        ).forEach((userOrTeamResource) => {
          const leafResource =
            schedulerRef.current.instance.project.resourceStore.getById(
              userOrTeamResource.id,
            );

          if (leafResource != null) {
            if (leafResource.type === SchedulerResourceType.User) {
              if (
                allTasksAssignees.users.find(
                  (x) => x.userId === leafResource.userId,
                ) == null
              ) {
                schedulerRef.current.instance.trigger(
                  'onUserRemovedFromProject',
                  {
                    projectId: leafResource.projectId,
                    userResource: {
                      type: ResourceType.User,
                      user: userLookup[leafResource.userId],
                    },
                  },
                );
              }
            } else if (leafResource.type === SchedulerResourceType.Team) {
              if (
                allTasksAssignees.teams.find(
                  (x) => x.teamId === leafResource.teamId,
                ) == null
              ) {
                schedulerRef.current.instance.trigger(
                  'onTeamRemovedFromProject',
                  {
                    projectId: leafResource.projectId,
                    teamId: leafResource.teamId,
                  },
                );
              }
            }
          }
        });
      }
    });
  }, [
    allTasksAssignees.teams,
    allTasksAssignees.users,
    project.projectId,
    userLookup,
  ]);
  const { removeAllExistingAndAutoFillFromTasks } =
    useRefreshAllocationsFromTasks({
      relativeDate,
      project,
      workingDaysConfig,
      userLookup,
      teamLookup,
      allTasksAssignees,
      schedulerRef,
      saveNewlyAllocatedMembersToState,
      isHoursPerWeek,
    });
  const { disableAutoAllocateForProject, enableAutoAllocateForProject } =
    useAutoAllocationStateManager({ groupBy: GroupBy.Projects, schedulerRef });
  const onSchedulerReady = useCallback(() => {
    if (autoAllocate) {
      removeAllExistingAndAutoFillFromTasks(true);
      removeTaskNotassignedMembers();
      if (!templateDirtyState?.state) {
        dispatch(
          setDirtyState({
            id: project.projectId,
            _state: false,
          }),
        );
      }
      enableAutoAllocateForProject(project.projectId);
    }
  }, [
    autoAllocate,
    removeAllExistingAndAutoFillFromTasks,
    removeTaskNotassignedMembers,
    templateDirtyState?.state,
    enableAutoAllocateForProject,
    project.projectId,
    dispatch,
  ]);

  useSchedulerInitialize({
    schedulerRef,
    projectInfoProvider: getSchedulerProjectInfo,
    relativeDate,
    onSchedulerReady,
    isHoursPerWeek,
  });
  const commonManagerProps = useCommonManagerProps({
    project,
    schedulerRef,
    workingDaysConfig,
    userLookup,
    teamLookup,
    teamMembership,
  });

  const updateAllocation = useCallback(
    (actionsMap: BulkActionPayloadMap) => {
      return new Promise<any>((resolve) => {
        const createPayload = actionsMap.create;
        const tempIds = Object.keys(createPayload);

        const newActionsMap = tempIds.reduce((map, id) => {
          const tempEvent =
            schedulerRef.current.instance.eventStore.getById(id);

          if (tempEvent) {
            const randomId = floatUid();
            const { [id]: tempAllocation, ...rest } = map.create;

            tempEvent.id = randomId;

            return {
              ...map,
              create: {
                ...rest,
                [randomId]: {
                  ...tempAllocation,
                  id: randomId,
                },
              },
            };
          }

          return map;
        }, actionsMap);

        dispatch(
          allocationsBulkAction({
            actionsMap: newActionsMap,
            relativeDate,
            isHoursPerWeek,
          }),
        );
        saveNewlyAllocatedMembersToState(newActionsMap);
        resolve({
          processSummaryUpdate: false,
          processTotalAllocationUpdate: true,
        });
      });
    },
    [saveNewlyAllocatedMembersToState, dispatch, relativeDate, isHoursPerWeek],
  );
  const {
    allocationModalParams,
    onEventActionCancel,
    onEventActionConfirm,
    onEventDelete,
    onEnableSplitMode,
  } = useAllocationManager({
    ...commonManagerProps,
    resourceManagementConfig,
    bulkUpdate: updateAllocation,
  });
  const { addResource, removeUserFromProjectInUI, removeTeamFromUI } =
    useResourceManager(commonManagerProps);
  const {
    addUsersModalProjectId,
    showAddUsersModal,
    hideAddUsersModal,
    onUsersAdded,
  } = useAddUsersManager({
    schedulerRef,
    project,
    addResource,
    allocatedMembers,
  });

  useRemoveUsersManager({
    schedulerRef,
    project,
    allocatedMembers,
    removeUserFromProjectInUI,
  });

  const {
    addTeamsModalProjectId,
    onTeamsAdded,
    showAddTeamsModal,
    hideAddTeamsModal,
    onTeamCreate,
  } = useAddTeamsManager({
    schedulerRef,
    project,
    addResource,
    allocatedMembers,
  });
  const {
    editTeamModalTeamId,
    showEditTeamModal,
    hideEditTeamModal,
    onTeamEdit,
  } = useEditTeamsManager({
    schedulerRef,
    project,
    allocatedMembers,
  });
  const { removeTeam } = useDeleteTeamsManager({
    project,
    allocatedMembers,
    removeTeamFromUI,
    teamLookup,
  });
  const columns = useColumnsConfig({
    schedulerRef,
    allTasksAssignees,
    onAddUsers: showAddUsersModal,
    onAddTeams: showAddTeamsModal,
    onTeamEdit: showEditTeamModal,
    userLookup,
  });

  useTeamRemovedFromProject(schedulerRef, ({ teamId }) => removeTeam(teamId));
  useEventsTooltip(schedulerRef, relativeDate, isHoursPerWeek);

  const currentTemplate = useActiveProjectTemplate();
  const saveAutoAllocateToState = useCallback(
    (isAutoAllocateOn) => {
      dispatch(
        updateProjectTemplate({
          ...currentTemplate,
          templateContent: {
            ...currentTemplate.templateContent,
            autoAllocation: isAutoAllocateOn,
          },
        }),
      );
    },
    [currentTemplate, dispatch],
  );
  const confirmForAutofill = useCallback(() => {
    confirm({
      title: translate('templates.resource_management.autofill.confirm.title'),
      content: translate(
        'templates.resource_management.autofill.confirm.message',
        { br: () => <br /> },
      ),
      okText: translate('autofill'),
      onOk: () => {
        removeAllExistingAndAutoFillFromTasks();
        removeTaskNotassignedMembers();
        saveAutoAllocateToState(true);
        enableAutoAllocateForProject(project.projectId);
      },
    });
  }, [
    enableAutoAllocateForProject,
    project.projectId,
    removeAllExistingAndAutoFillFromTasks,
    removeTaskNotassignedMembers,
    saveAutoAllocateToState,
    translate,
  ]);

  const onAutoAllocateChange = useCallback(
    (isAutoAllocateOn) => {
      if (isAutoAllocateOn) {
        confirmForAutofill();
      } else {
        saveAutoAllocateToState(isAutoAllocateOn);
        disableAutoAllocateForProject(project.projectId);
      }
    },
    [
      confirmForAutofill,
      disableAutoAllocateForProject,
      project.projectId,
      saveAutoAllocateToState,
    ],
  );
  const {
    getSourceTasksForAllocation,
    resetAllocationForShowingSourceTasks,
    allocationForShowingSourceTasks,
  } = useAutoAllocateSourceTasks({
    schedulerRef,
    relativeDate,
    isHoursPerWeek,
  });

  return (
    <>
      <FilterBar>
        <FilterBar.Secondary>
          <AutoAllocateSwitch
            checked={autoAllocate}
            onChange={onAutoAllocateChange}
          />
          <Toolbar
            zoomIn={() => {
              schedulerRef.current.instance.zoomIn();
            }}
            zoomOut={() => {
              schedulerRef.current.instance.zoomOut();
            }}
            shiftNext={() => {
              schedulerRef.current.instance.scrollToDate(
                moment
                  .min(
                    moment(
                      schedulerRef.current?.instance.visibleDateRange.endDate,
                    ).add(1, 'day'),
                    moment(schedulerConfiguration.endDate),
                  )
                  .toDate(),
              );
            }}
            shiftPrevious={() => {
              schedulerRef.current.instance.scrollToDate(
                moment
                  .max(
                    moment(
                      schedulerRef.current?.instance.visibleDateRange.startDate,
                    ).subtract(1, 'day'),
                    moment(schedulerConfiguration.startDate),
                  )
                  .toDate(),
              );
            }}
          />
        </FilterBar.Secondary>
      </FilterBar>
      <Scheduler
        ref={schedulerRef}
        columns={columns}
        newEventAllowedOn={[
          SchedulerResourceType.User,
          SchedulerResourceType.Team,
        ]}
        schedulerConfig={schedulerConfiguration}
        zoomPresets={viewPresets}
        workingDaysConfig={workingDaysConfig}
        resourceManagementConfig={resourceManagementConfig}
      />
      {allocationModalParams != null ? (
        <ManageAllocationDrawer
          hideAllocationMediumControls
          softAllocationsEnabled={false}
          userLookup={userLookup}
          projectLookup={projectLookup}
          teamLookup={teamLookup}
          allocationDetail={allocationModalParams}
          onClose={onEventActionCancel}
          onDone={onEventActionConfirm}
          onDelete={onEventDelete}
          onEnableSplitMode={onEnableSplitMode}
          workingDaysConfig={workingDaysConfig}
          isHoursPerWeek={isHoursPerWeek}
        />
      ) : null}
      <AddUsersModal
        projectId={addUsersModalProjectId}
        projectLookup={projectLookup}
        onUsersAdded={onUsersAdded}
        onCancel={hideAddUsersModal}
        renderPrimaryButtonContent={(count) => (
          <FormattedMessage
            id="templates.resource_management.actions.add_team_member"
            values={{ count }}
          />
        )}
      />
      <AddTeamsModal
        projectLookup={projectLookup}
        teamMembership={teamMembership}
        projectId={addTeamsModalProjectId}
        onInvited={onTeamsAdded}
        createTeamHandler={onTeamCreate}
        teamsAvailableForProject={Object.values(teamLookup)}
        onCancel={hideAddTeamsModal}
      />
      {editTeamModalTeamId ? (
        <PlaceholderCreateOrEditModal
          team={teamLookup[editTeamModalTeamId]}
          roles={roles}
          placeholders={Object.values(teamLookup)}
          primaryButtonLabel={<FormattedMessage id="save" />}
          save={onTeamEdit}
          cancel={hideEditTeamModal}
        />
      ) : null}
      <AutoAllocateSourceTasksDrawer
        userLookup={userLookup}
        teamLookup={teamLookup}
        allocation={allocationForShowingSourceTasks}
        sourceTasksGetter={getSourceTasksForAllocation}
        onCancel={resetAllocationForShowingSourceTasks}
        isHoursPerWeek={isHoursPerWeek}
      />
    </>
  );
};
